import React, { useCallback, useEffect } from 'react';
import { Field, Form } from 'react-final-form';
import styled from 'styled-components';
import moment from 'moment';
import { FormApi, ValidationErrors } from 'final-form';

import {
  compose,
  optionalSirenValidator,
  required,
  sirenValidator,
  UtcValidator,
} from '@front/common/src/forms/validations';
import { getDateInputConfig } from '@front/common/src/forms/validators/date';
import { isCountryGB } from '@front/common/src/utils/country';
import { UniverseReference } from '@front/common/src/types/commons';
import colors from '@front/common/src/utils/colors';

import { useValidateUniqueSiren } from '@front/pro/src/queries/account-queries';
import { useUniverse, useWorkingInformation } from '@front/pro/src/signup/signup-form-context';
import { WorkingInformationFR, WorkingInformationUK } from '@front/pro/src/signup/types';
import { useCompanyStatus } from '@front/pro/src/hooks/use-company-status';
import { useIsWorkingInFrance } from '@front/pro/src/hooks/use-is-working-in-france';
import { usePermitResidentType } from '@front/pro/src/hooks/use-permit-resident-type';
import { useNovaStatus } from '@front/pro/src/hooks/use-nova-status';
import { useWillWorkAsSelfEmployed } from '@front/pro/src/hooks/use-will-work-as-self-employed';
import { useGbResidenceStatus } from '@front/pro/src/hooks/use-gb-residence-status';
import { SIREN_REGISTRATION_LINK } from '@front/pro/src/signup/utils';

import { translate } from '@front/i18n';

import Button from '@front/web-ui/src/components/button/button';
import { InputAdapter, SelectAdapter } from '@front/web-ui/src/forms/adapters';
import Alert from '@front/web-ui/src/components/alert/alert';
import SimpleLink from '@front/web-ui/src/components/link/simple-link';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import { Screen } from '../../components/screen/screen';
import ButtonBar from '../../components/button-bar/button-bar';
import { getRoute } from '../../routes/routes';
import { ConditionalField } from '../../components/conditional-field/conditional-field';
import { useNavigateHistoryWithQuery } from '../../hooks/use-navigate-history-with-query';
import { logSignupStepEvent } from '../../utils/tracking';
import { useWebStepper } from '../../hooks/use-web-stepper';
import { useRedirectionStep } from '../../hooks/use-redirection-step';

import { dateValidator } from './validation';

const t = translate('screens.professional_status');

const StyledInputAdapter = styled(InputAdapter)<{ $marginBottom?: number }>`
  margin-bottom: ${({ $marginBottom }) => -15 + ($marginBottom || 0)}px;
`;

const StyledAlert = styled(Alert)`
  margin-bottom: 20px;
`;

const StyledTextWithLink = styled.div`
  margin-top: -10px;
  display: flex;
  gap: 5px;

  > p {
    display: inline;
    margin: 0;
  }
`;

const { mask: dateInputMask, mode, type } = getDateInputConfig();

type ProfessionalStatusFieldsProps<T> = {
  values: T;
  errors: ValidationErrors;
  form: FormApi;
  universe?: UniverseReference;
};

const ProfessionalStatusFieldsFR = ({
  values,
  errors,
  form,
  universe,
}: ProfessionalStatusFieldsProps<WorkingInformationFR>) => {
  useRedirectionStep();
  const companyStatusOptions = useCompanyStatus({ universe: universe || 'cleaning' });
  const isWorkingFranceOptions = useIsWorkingInFrance();
  const permitResidentTypeOptions = usePermitResidentType();
  const novaStatusOptions = useNovaStatus();

  return (
    <>
      <Field
        validate={required('company_status')}
        $marginBottom={10}
        message={universe === 'cleaning' ? t('.company_status_dropdown.message') : undefined}
        component={SelectAdapter}
        id='company_status'
        label={t('.company_status_dropdown.placeholder')}
        options={companyStatusOptions}
        name='company_status'
      />
      {universe !== 'cleaning' && (
        <>
          <ConditionalField
            validate={sirenValidator('siren')}
            component={InputAdapter}
            changeValue={form.change}
            name='siren'
            id='siren'
            type='text'
            error={errors?.siren}
            label={t('.siren.label_mandatory')}
          />
          <StyledTextWithLink>
            <Text color={colors.ink50} htmlTag='p'>
              {t('.no_siren').toUpperCase()}
            </Text>
            <SimpleLink openInNewTab href={SIREN_REGISTRATION_LINK}>
              {t('.register_for_siren')}
            </SimpleLink>
          </StyledTextWithLink>
        </>
      )}
      {values.company_status && values.company_status !== 'no_company' && universe === 'cleaning' && (
        <>
          <ConditionalField
            validate={optionalSirenValidator('siren')}
            component={InputAdapter}
            changeValue={form.change}
            name='siren'
            id='siren'
            type='text'
            error={errors?.siren}
            label={t('.siren.label_optional')}
          />
          <ConditionalField
            validate={required('nova_status')}
            changeValue={form.change}
            component={SelectAdapter}
            options={novaStatusOptions}
            name='nova_status'
            id='nova_status'
            label={t('.nova_status.label')}
          />
        </>
      )}
      {values.company_status === 'no_company' && (
        <ConditionalField
          validate={required('can_work_in_france')}
          changeValue={form.change}
          $marginBottom={15}
          component={SelectAdapter}
          id='can_work_in_france'
          label={t('.can_work_in_france.label')}
          options={isWorkingFranceOptions}
          name='can_work_in_france'
        />
      )}
      {values.can_work_in_france === 'yes_resident' && (
        <>
          <ConditionalField
            validate={required('residence_permit_type')}
            changeValue={form.change}
            $marginBottom={15}
            component={SelectAdapter}
            id='residence_permit_type'
            name='residence_permit_type'
            label={t('.residence_permit_type.label')}
            options={permitResidentTypeOptions}
          />
          <ConditionalField
            changeValue={form.change}
            placeholder={moment().add(3, 'months').format('DD/MM/YYYY')}
            component={StyledInputAdapter}
            mask={dateInputMask}
            $marginBottom={15}
            inputMode={mode}
            type={type}
            validate={compose(
              required('residence_permit_expiration_date'),
              dateValidator('residence_permit_expiration_date'),
            )}
            id='residence_permit_expiration_date'
            name='residence_permit_expiration_date'
            label={t('.residence_permit_expiration_date.label')}
          />
        </>
      )}
    </>
  );
};

const ProfessionalStatusFieldsUK = ({
  errors,
  form,
  universe,
}: Omit<ProfessionalStatusFieldsProps<WorkingInformationUK>, 'values'>) => {
  const gbResidenceStatusOptions = useGbResidenceStatus();
  const willWorkAsSelfEmployedOptions = useWillWorkAsSelfEmployed();
  return (
    <>
      <Field
        $marginBottom={10}
        validate={UtcValidator('siren')}
        component={InputAdapter}
        changeValue={form.change}
        name='siren'
        id='siren'
        type='text'
        error={errors?.siren}
        label={t('.siren.label_optional')}
      />
      {universe === 'cleaning' && (
        <>
          <Field
            validate={required('will_work_as_self_employed')}
            component={SelectAdapter}
            id='will_work_as_self_employed'
            label={t('.will_work_as_self_employed.label')}
            options={willWorkAsSelfEmployedOptions}
            name='will_work_as_self_employed'
          />
          <Field
            validate={required('gb_residence_status')}
            $marginBottom={10}
            component={SelectAdapter}
            id='gb_residence_status'
            label={t('.gb_residence_status.label')}
            options={gbResidenceStatusOptions}
            name='gb_residence_status'
          />
        </>
      )}
    </>
  );
};

const ProfessionalStatus = () => {
  const { workingInformation, setWorkingInformation } = useWorkingInformation();
  const { nextStep } = useWebStepper();
  const { validateUniqueSiren, validateSirenError } = useValidateUniqueSiren();
  const { historyPushWithQuery } = useNavigateHistoryWithQuery();
  const { universe } = useUniverse();

  useEffect(() => {
    // In some browser, focus on the input is not enough to view the title of the page so we are scrolling to the top
    // eslint-disable-next-line no-multi-assign
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, []);

  const onSubmit = useCallback(
    (values: WorkingInformationFR | WorkingInformationUK) => {
      if (values.siren) {
        validateUniqueSiren(
          { siren: values.siren },
          {
            onSuccess: () => {
              setWorkingInformation(values);
              logSignupStepEvent({ step: nextStep, account: values, universe });
              historyPushWithQuery(getRoute(nextStep));
            },
          },
        );
        return;
      }
      setWorkingInformation(values);
      logSignupStepEvent({ step: nextStep, account: values, universe });
      historyPushWithQuery(getRoute(nextStep));
    },
    [historyPushWithQuery, nextStep, setWorkingInformation, validateUniqueSiren, universe],
  );

  return (
    <Screen title={t('.title')} isCenteredOnDesktop={false}>
      {validateSirenError && <StyledAlert content={validateSirenError} type='error' />}
      <Form onSubmit={onSubmit} validateOnBlur={false} initialValues={workingInformation}>
        {({ handleSubmit, errors, values, form }) => (
          <form onSubmit={handleSubmit}>
            {isCountryGB() ? (
              <ProfessionalStatusFieldsUK form={form} errors={errors} universe={universe} />
            ) : (
              <ProfessionalStatusFieldsFR values={values} form={form} errors={errors} universe={universe} />
            )}
            <ButtonBar>
              <Button size='lg' type='submit'>
                {t('base.next')}
              </Button>
            </ButtonBar>
          </form>
        )}
      </Form>
    </Screen>
  );
};

export default ProfessionalStatus;
